import { customElement, property, query } from 'lit/decorators.js';
import {html, LitElement} from "lit";
import {modalStyle} from "@/components/modal/modal.styles";
import {SbkButton} from "@/components/button/button.ts";
import {SbkForm} from "@/components/form/form";
import './modal.scss';

/**
 * An example element.
 *
 * @slot - This element has a slot
 */
@customElement('sbk-modal')
export class SbkModal extends LitElement {

    cancelButtonId = 'cancel';

    submitButtonId = 'submit';

    @property()
    heading = '';

    @property({type: Boolean})
    open = false;

    @property({type: Boolean})
    slotContent = false;

    @property({type: Boolean})
        // eslint-disable-next-line lit/attribute-names
    hideCloseButton = false;

    @property()
    icon: 'warning' | 'success' | null = null;

    @query('slot[name="footer"]') _footerSlot!: HTMLSlotElement;

    showModal() {
        this.open = true;
        const modal = this.renderRoot.querySelector('dialog');
        if (modal) {
            modal.showModal();
            modal.addEventListener('click', this.closeModalOnBackdropClick);
        }
        document.body.classList.add('no-scroll');
    }

    private closeModalOnBackdropClick = (event: MouseEvent) => {
        const target = <HTMLElement> event.target;
        if (target && target.nodeName === 'DIALOG') {
            const modal = this.renderRoot.querySelector('dialog');
            if (modal) {
                modal.removeEventListener('click', this.closeModalOnBackdropClick);
                modal.close();
                document.body.classList.remove('no-scroll');
            }
        }
    }

    async _showModal(e: Event) {
        e.preventDefault();
        this.showModal()
        await this.updateComplete
        const modalOpenEvent = new CustomEvent('modal-open');
        this.dispatchEvent(modalOpenEvent);
    }

    _closeModal(e: Event) {
        this.open = false;
        const modalBeforeCloseEvent = new CustomEvent('modal-before-close',
            {
                bubbles: true,
                cancelable: true,
                detail: {id: this.id},
            });
        this.dispatchEvent(modalBeforeCloseEvent);
        if (modalBeforeCloseEvent.defaultPrevented) {
            e.preventDefault();
        }
        const modal = this.renderRoot.querySelector('dialog');
        if (modal && !modalBeforeCloseEvent.defaultPrevented) {
            modal.removeEventListener('click', this.closeModalOnBackdropClick);
            modal.close();
        }

        // the modal cannot be found when the cancel button in the footer slot is clicked
        // therefore you need to query the dialog from the parent element shadow root
        if (!modal && !modalBeforeCloseEvent.defaultPrevented) {
            const secondModal = this.parentElement?.shadowRoot?.querySelector('dialog')
            secondModal?.close();
        }

        document.body.classList.remove('no-scroll');
        const modalClosedEvent = new CustomEvent('modal-closed', {
            detail: {id: this.id},
        });
        this.dispatchEvent(modalClosedEvent);
    }

    static get styles() {
        return modalStyle;
    }

    firstUpdated() {
        this.slotContent = this._hasSlotContent();
        if (this.open) {
            this.showModal();
        }

        if (this.slotContent) {
            this._cancelButton();
            this._submitButton();
        }
    }

    _renderIcon() {
        switch (this.icon) {
            case 'warning':
                return html`
                    <sbk-icon size="112" symbol='modal-warning'></sbk-icon>
                `;
            case 'success':
                return html`
                    <sbk-icon size="112" symbol='modal-success'></sbk-icon>
                `;
            default:
                return null;
        }
    }

    render() {
        return html`
            <slot name="triggerElement" @click=${this._showModal}></slot>
            <dialog part="dialog" class="modal inner-border">
                <header class="modal__header">
                    ${this.heading ? html`<h3 tabindex="0">${this.heading}</h3>` : ''}
                    ${!this.hideCloseButton ? html`
                        <sbk-round-button size="medium" variant="round" icon="close" class="close-button"
                                    @click=${this._closeModal}></sbk-round-button>` : ''}
                </header>
                <div class="modal__content">
                    ${this.icon ? html`
                        <div class="modal__content--icon-wrapper">${this._renderIcon()}</div>` : ''}
                    <slot></slot>
                    <slot name="settings" class="modal__content--settings"></slot>
                    <slot name="link"></slot>
                </div>
                <footer class="modal__footer ${!this.slotContent ? 'modal__footer--hidden' : ''}">
                    <slot name="footer"></slot>
                </footer>
            </dialog>
        `;
    }

    _hasSlotContent() {
        const footerSlot = this.shadowRoot?.querySelector('slot[name="footer"]') as HTMLSlotElement;
         
        return footerSlot?.assignedNodes({flatten: true}).length > 0 || false;
    }

    _cancelButton() {
        const footerSlot = this.shadowRoot?.querySelector('slot[name="footer"]') as HTMLSlotElement;
         
        return footerSlot?.assignedElements({flatten: true}).forEach(button => {
            if (this.cancelButtonId === button.id && button instanceof SbkButton) {
                button.addEventListener('click', this._closeModal)
            }
        });
    }

    _submitButton() {
        const footerSlot = this.shadowRoot?.querySelector('slot[name="footer"]') as HTMLSlotElement;
         
        return footerSlot?.assignedElements({flatten: true}).forEach(button => {
            if (this.submitButtonId === button.id && button instanceof SbkButton) {
                button.addEventListener('click', this._triggerFormSubmit)
            }
        });
    }

    _triggerFormSubmit() {
        const form = this.parentElement?.querySelector('sbk-form') as SbkForm
        form?._submitForm();
    }
}
